import axios from "axios";

// Local dedup key: holds the last app-info payload we successfully sent, so we
// only re-POST when something actually changes (mirrors the backend's own dedup).
// Cleared on logout alongside the other supervisor tokens in api-client.ts.
const APP_INFO_KEY = "user-app-info";

// NOTE: confirm the exact route/casing with the backend. The API doc writes it
// as `POST /api/users/app-info`; REACT_APP_API_END_POINT already includes the
// `/api` suffix, and existing user endpoints are PascalCase (e.g.
// `/Users/Refresh`). Adjust this single constant if the real path differs —
// this call is fire-and-forget analytics, so a wrong path fails silently.
const APP_INFO_ENDPOINT = `${process.env.REACT_APP_API_END_POINT}/Users/app-info`;

interface AppInfoPayload {
	platform: string;
	appVersion: string;
	deviceType: string;
	osVersion: string;
	userAgent: string;
}

const buildAppInfoPayload = (): AppInfoPayload => {
	const userAgent = (navigator.userAgent || "").slice(0, 500);
	return {
		platform: "Web", // max 50
		// No real runtime version exists in this app yet. Wire REACT_APP_VERSION
		// in environments/*.env (or import package.json) to populate this.
		appVersion: (process.env.REACT_APP_VERSION || "").slice(0, 50), // max 50
		deviceType: (navigator.platform || "").slice(0, 100), // max 100, e.g. "MacIntel"
		osVersion: "", // not reliably derivable without a UA-parser dependency
		userAgent, // max 500
		// ipAddress intentionally omitted — captured server-side if ever needed,
		// to avoid collecting GDPR/CCPA-regulated PII from the client.
	};
};

/**
 * Fire-and-forget sync of browser/app context for supervisor analytics. Safe to
 * call on app open, on login and on tab foreground. Never throws and never gates
 * app functionality: it no-ops without a supervisor JWT and swallows all errors.
 */
export const syncUserAppInfo = async (): Promise<void> => {
	try {
		const token = localStorage.getItem("token");
		if (!token) return; // needs a valid supervisor JWT

		const payload = buildAppInfoPayload();
		const serialized = JSON.stringify(payload);

		if (localStorage.getItem(APP_INFO_KEY) === serialized) return; // unchanged

		// Fresh, unintercepted instance so this analytics call never triggers the
		// admin client's 401 refresh/logout interceptors on an incidental failure.
		const uninterceptedAxiosInstance = axios.create();
		await uninterceptedAxiosInstance.post(APP_INFO_ENDPOINT, payload, {
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`, // supervisor calls use the Bearer prefix
			},
		});

		// Persist only after a successful send, so a failed attempt retries later.
		localStorage.setItem(APP_INFO_KEY, serialized);
	} catch (error) {
		// Analytics-only: must never surface to the user or block anything.
	}
};
